/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue } from "recoil";
import { ArchiveAPI } from "../../../api/Archive";
import { UserAtom } from "../../../atoms/UserAtom";
import { UserEmailArchiveAtom } from "../../../atoms/UserEmailArchiveAtom";
import { IArchive } from "../../../generated/Api";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import { useModal } from "../Modal";

const ModalBody = styled.div`
  width: 580px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #eceeee;

  margin: 16px -24px 0px;
`;

const Wrapper = styled.div`
  width: 100%;
  height: fit-content;

  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;

  padding: 32px 24px;
`;

const Buttons = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  border-top: 1px solid #eceeee;
  padding: 24px 24px 0px;
`;

const InputLabel = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
  margin-bottom: 12px;
`;

const Subtitle = styled.div`
  margin-top: 9px;
  font: normal normal normal 14px Source Sans Pro;
  color: #707070;
`;

const PublicUrlInput = styled.div`
  display: grid;
  grid-template-columns: max-content 5px 1fr;
  grid-gap: 12px;
  height: 40px;
  width: 100%;
  background-color: #f4f4f4;
  border-radius: 4px 0px 0px 4px;
  border: 1px solid #cccccc;
  padding: 10px 13px;
`;

const Divider = styled.div`
  width: 1px;
  border-right: 1px solid #ccc;
  margin: -9px 0px;
`;

export default function UpdateArchiveTitle(): JSX.Element | null {
  const [disable, setDisable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const user = useRecoilValue(UserAtom);
  const [userEmailArchive, setUserEmailArchive] =
    useRecoilState(UserEmailArchiveAtom);
  const [archiveTitle, setArchiveTitle] = useState("");
  const modal = useModal();

  function checkInput(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.value.length < 1) setDisable(true);
    else if (disable && event.target.value.length >= 1) setDisable(false);
  }

  async function updateEmailArchive(archive: IArchive) {
    setIsLoading(true);
    let updatedArchiveResponse = await ArchiveAPI.update({
      userUid: user.userUid,
      archive,
    });

    let updatedArchive = updatedArchiveResponse;

    if (!updatedArchive) {
      toast("Error when updating the settings", { type: "error" });
      return;
    }

    setUserEmailArchive(updatedArchive as IArchive);
    setIsLoading(false);
    modal.popModal();
  }

  async function updateArchiveTitle() {
    if (archiveTitle.trim() === userEmailArchive.title)
      if (archiveTitle.length < 4) {
        toast("Your archive title must have at least 4 letters", {
          type: "info",
        });
        return;
      }

    if (archiveTitle.length > 45) {
      toast("Your archive title must have at most 45 letters", {
        type: "info",
      });
      return;
    }

    updateEmailArchive({
      ...userEmailArchive,
      title: archiveTitle,
    });
  }

  if (!user) return <></>;
  return (
    <ModalBody>
      <Wrapper>
        <InputLabel>
          <Title>Public Archive Title</Title>
          <StyledInput
            placeholder="Enter your public archive title"
            defaultValue={userEmailArchive?.title}
            onChange={(evt) => {
              checkInput(evt);
              setArchiveTitle(evt.target.value);
            }}
            onKeyDown={(evt) => {
              if (evt.key === "Enter") {
                evt.preventDefault();
                updateArchiveTitle();
              }
            }}
          />
        </InputLabel>

        <InputLabel>
          <Title style={{ marginTop: "19px" }}>Public Archive URL</Title>
          <PublicUrlInput>
            <div>archive.berserkermail.com/</div>
            <Divider />
            <div>{user.userAppName}</div>
          </PublicUrlInput>
        </InputLabel>
        <Subtitle>
          Your unique app name is used in your Public Archive URL. To change it,
          please contact us via Helpdesk.
        </Subtitle>
      </Wrapper>

      <Buttons>
        <StyledButton
          inverted
          onClick={() => {
            setArchiveTitle(userEmailArchive.title);
            modal.popModal();
          }}
          css={css`
            width: 80px;
            margin-right: 12.5px;
          `}
        >
          Cancel
        </StyledButton>
        <StyledButton
          disable={disable}
          isLoading={isLoading}
          onClick={() => updateArchiveTitle()}
          css={css`
            width: 80px;
          `}
        >
          Save
        </StyledButton>
      </Buttons>
    </ModalBody>
  );
}
